/**
 * Sharing the phone's sound with the player's own music (Spotify, Apple Music…): the game's sound plays over it,
 * never stops it.
 *  - iPhone: anything played through an <audio>/<video> element (the street's song, the car radio, the TV) puts the
 *    page in the 'playback' audio category, and iOS pauses every other app's music. Safari 16.4+ lets a page say its
 *    sound is 'ambient' instead (mixes with other music; silenced by the ring/silent switch, like most games), which
 *    covers every element and the WebAudio sound effects alike. The mic needs 'play-and-record' while it's on.
 *  - Android, and older iPhones without that: a long <audio> takes the phone's audio focus and the other music
 *    stops; nothing a page can do about it. There, music that would start on its own (the street's song) doesn't;
 *    what the player switches on themselves (the radio, a clip, the TV) still plays.
 */
type AudioSession = { type: string };
const session = typeof navigator !== 'undefined' ? (navigator as Navigator & { audioSession?: AudioSession }).audioSession : undefined;
const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
export const phone = /iPhone|iPad|iPod|Android/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1); // (an iPad says it's a Mac)

const set = (type: string) => { try { if (session) session.type = type; } catch { /* not allowed: leave it */ } };

/**
 * The player's choice, where the page can choose (iPhone): 'mix' plays alongside their music, but the silent switch
 * mutes it (iOS's rule for sound that mixes); 'loud' plays through the switch, and other music pauses while it does.
 */
const MIX_KEY = 'unfold.soundMix';
let mix = (() => { try { return localStorage.getItem(MIX_KEY) !== 'loud'; } catch { return true; } })();
/** the choice is there to make (a browser that lets the page say which: iPhone/iPad) */
export const canChooseMix = !!session && phone;
export const soundMixes = () => mix;
export function setSoundMix(on: boolean) {
  mix = on;
  try { localStorage.setItem(MIX_KEY, on ? 'mix' : 'loud'); } catch { /* storage blocked: this session only */ }
  if (!micOn) set(on ? 'ambient' : 'playback');
}

/** At startup, before any sound: the game's sound mixes with the player's music (where the browser lets a page say so). */
export function shareAudio() { set(mix ? 'ambient' : 'playback'); }

/** Whether the game's music can play without stopping the player's own: false on phones that can't mix it. */
export const musicMixes = !!session || !phone;

/** The mic on (voice chat) needs the recording category; off again, back to sharing. */
let micOn = false;
export function micInUse(on: boolean) { micOn = on; set(on ? 'play-and-record' : mix ? 'ambient' : 'playback'); }

/**
 * Game music on or off (Phone → Support; per device): off, the street's song and the club's music go silent, the sound
 * effects stay, for the player's own music. (Nothing in a browser can tell if Spotify is playing, so it's the player's
 * call.) The club's track still runs muted: the lights and the dance floor keep its beat.
 */
const KEY = 'unfold.gameMusic';
let musicOn = (() => { try { return localStorage.getItem(KEY) !== 'off'; } catch { return true; } })();
const subs = new Set<(on: boolean) => void>();
export const gameMusic = () => musicOn;
export function setGameMusic(on: boolean) {
  musicOn = on;
  try { localStorage.setItem(KEY, on ? 'on' : 'off'); } catch { /* storage blocked: this session only */ }
  subs.forEach((f) => f(on));
}
export function onGameMusic(f: (on: boolean) => void) { subs.add(f); return () => { subs.delete(f); }; }
